/**
 * The geometry of a picture inside its frame — "cover" fit, zoom, pan, quarter turns, mirroring
 * and the fine `straightenDeg` rotation — as pure numbers.
 *
 * The renderer, the editor's crop gesture, the export planner and the readiness check all take
 * their figures from here, so the crop a person drags is the crop that prints and the DPI warning
 * is about the pixels that will really be stretched.
 *
 * Units are whatever the caller passes for the box (mm or px); the image side is always pixels.
 *
 * Straightening: the image is rotated by θ about the frame centre. For the rotated image to cover
 * an axis-aligned W × H frame, its own half-extents must reach the frame's corners once those are
 * turned back into image space, i.e. the image must be at least
 *     (W·|cos θ| + H·|sin θ|) wide  and  (W·|sin θ| + H·|cos θ|) tall.
 * The pan is stored as a fraction of the overflow along the IMAGE axes, which keeps `offsetX/Y`
 * meaning "how far off centre" whatever the angle, and reduces to the plain formula at θ = 0.
 */
import type { ImageFit } from './schema';

export interface ImageCoverInput {
  boxWidth: number;
  boxHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  fit: ImageFit;
}

export interface ImageCoverGeometry {
  /** natural size after the quarter turn */
  nw: number;
  nh: number;
  /** box units per image pixel at `fit.scale` (cover × zoom) */
  scale: number;
  /** size of the rotated-by-90 image at that scale, in box units */
  width: number;
  height: number;
  /** slack along the image axes once the frame is covered; the pan range is ± half of this */
  overflowX: number;
  overflowY: number;
  /** pan along the image axes, in box units */
  panX: number;
  panY: number;
  /** the same pan in frame axes — what the image centre is offset by on screen */
  tx: number;
  ty: number;
  /** total rotation applied by the transform (straighten + quarter turns), degrees clockwise */
  angleDeg: number;
}

/** A photo freshly placed in a frame: cover fit, centred, upright. */
export function neutralImageFit(): ImageFit {
  return { scale: 1, offsetX: 0, offsetY: 0, rotate90: 0, flipX: false, flipY: false, straightenDeg: 0 };
}

const clamp1 = (n: number): number => Math.min(1, Math.max(-1, n));
const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Cover scale (box units per image pixel, before zoom) for a frame the rotated picture must fill. */
export function imageCoverBase(input: ImageCoverInput): number {
  const { boxWidth: W, boxHeight: H, fit } = input;
  const quarter = fit.rotate90 === 90 || fit.rotate90 === 270;
  const nw = Math.max(1, quarter ? input.naturalHeight : input.naturalWidth);
  const nh = Math.max(1, quarter ? input.naturalWidth : input.naturalHeight);
  const theta = rad(fit.straightenDeg ?? 0);
  const c = Math.abs(Math.cos(theta));
  const s = Math.abs(Math.sin(theta));
  return Math.max((W * c + H * s) / nw, (W * s + H * c) / nh);
}

/** Box units one source pixel occupies once the picture is fitted and zoomed. */
export function imageCoverScale(input: ImageCoverInput): number {
  return imageCoverBase(input) * Math.max(1, input.fit.scale);
}

export function imageCoverGeometry(input: ImageCoverInput): ImageCoverGeometry {
  const { boxWidth: W, boxHeight: H, fit } = input;
  const quarter = fit.rotate90 === 90 || fit.rotate90 === 270;
  const nw = Math.max(1, quarter ? input.naturalHeight : input.naturalWidth);
  const nh = Math.max(1, quarter ? input.naturalWidth : input.naturalHeight);
  const straighten = fit.straightenDeg ?? 0;
  const theta = rad(straighten);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const c = Math.abs(cos);
  const s = Math.abs(sin);

  const scale = imageCoverBase(input) * fit.scale;
  const width = nw * scale;
  const height = nh * scale;
  const overflowX = Math.max(0, width - (W * c + H * s));
  const overflowY = Math.max(0, height - (W * s + H * c));

  // offset 0 = centred, +1 = the picture pushed fully towards the start of the axis
  const panX = (fit.offsetX * overflowX) / 2;
  const panY = (fit.offsetY * overflowY) / 2;
  const tx = panX * cos - panY * sin;
  const ty = panX * sin + panY * cos;

  return { nw, nh, scale, width, height, overflowX, overflowY, panX, panY, tx, ty, angleDeg: straighten + fit.rotate90 };
}

/** A point of interest in the picture, in natural image coordinates: 0..1 from the top left. */
export interface FocalPoint {
  x: number;
  y: number;
}

export interface FocalFitResult {
  fit: ImageFit;
  /** Whether the point really reached the middle, or the crop ran out of room first. */
  centred: boolean;
  /**
   * How far off centre it ended up, as a fraction of the frame's half-size: 0 is dead centre, 1 is
   * at the frame's edge. What "do the best you can" actually achieved, in a number a UI can judge.
   */
  missedBy: number;
}

/** Where a natural-image point lands once the fit's quarter turn and mirroring are applied. */
function orientPoint(focal: FocalPoint, fit: ImageFit): FocalPoint {
  const x = Math.min(1, Math.max(0, focal.x));
  const y = Math.min(1, Math.max(0, focal.y));
  let p: FocalPoint;
  switch (fit.rotate90) {
    case 90: p = { x: 1 - y, y: x }; break;
    case 180: p = { x: 1 - x, y: 1 - y }; break;
    case 270: p = { x: y, y: 1 - x }; break;
    default: p = { x, y };
  }
  return { x: fit.flipX ? 1 - p.x : p.x, y: fit.flipY ? 1 - p.y : p.y };
}

/**
 * The fit that puts a point of interest in the middle of the frame — and an honest answer about
 * whether it managed.
 *
 * A "cover" crop only has as much room to move as the picture overhangs the frame, so a subject
 * near an edge often cannot reach the middle: a face at 10% across a picture that overhangs by a
 * tenth can travel a tenth, not four. Rather than pretend, the pan is clamped exactly as dragging
 * would clamp it, `centred` says whether the point arrived, and `missedBy` says how far short it
 * stopped. The caller decides what to do with that — the auto-fill flags it rather than silently
 * shipping a cropped-off head.
 *
 * Zoom, straightening and quarter turns in the incoming fit are respected, so this re-centres an
 * adjusted picture as readily as it places a fresh one.
 */
export function focalImageFit(input: ImageCoverInput & { focal?: FocalPoint | null }): FocalFitResult {
  const { focal, fit } = input;
  const g = imageCoverGeometry(input);
  if (!focal) return { fit, centred: true, missedBy: 0 };

  const point = orientPoint(focal, fit);
  // Where the point sits relative to the picture's own centre, in box units along each image axis.
  const fromCentreX = (point.x - 0.5) * g.width;
  const fromCentreY = (point.y - 0.5) * g.height;
  // The pan that would bring it to the middle, expressed the way `offset` is: ±1 of the overflow.
  // With no overflow on an axis there is nothing to pan, and the subject stays where it is.
  const offsetX = g.overflowX > 0.01 ? clamp1((-2 * fromCentreX) / g.overflowX) : 0;
  const offsetY = g.overflowY > 0.01 ? clamp1((-2 * fromCentreY) / g.overflowY) : 0;

  /*
   * Measure where the subject actually ended up, rather than inferring it from the clamp. The two
   * agree when the crop simply ran out of room, but only this catches the other way of failing:
   * an axis with no overflow at all, where the pan is not clamped because there was never any pan
   * to clamp, and a subject at the top of the frame stays at the top.
   */
  const theta = rad(fit.straightenDeg ?? 0);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const panX = (offsetX * g.overflowX) / 2;
  const panY = (offsetY * g.overflowY) / 2;
  const residualX = (fromCentreX + panX) * cos - (fromCentreY + panY) * sin;
  const residualY = (fromCentreX + panX) * sin + (fromCentreY + panY) * cos;
  const halfW = Math.max(0.01, input.boxWidth / 2);
  const halfH = Math.max(0.01, input.boxHeight / 2);
  const missedBy = Math.hypot(residualX / halfW, residualY / halfH);

  return {
    fit: { ...fit, offsetX, offsetY },
    centred: missedBy < 0.02,
    missedBy,
  };
}

/**
 * Drag the picture by (dx, dy) in FRAME units and return the fit that shows it there, clamped so
 * the frame never shows a gap. The delta is turned back into image axes first, so dragging right
 * still moves the picture right when it is straightened.
 */
export function panImageFit(input: ImageCoverInput, dx: number, dy: number): ImageFit {
  const g = imageCoverGeometry(input);
  const theta = rad(input.fit.straightenDeg ?? 0);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const dpx = dx * cos + dy * sin;
  const dpy = -dx * sin + dy * cos;
  const { fit } = input;
  return {
    ...fit,
    offsetX: g.overflowX > 0.01 ? clamp1(fit.offsetX + (2 * dpx) / g.overflowX) : fit.offsetX,
    offsetY: g.overflowY > 0.01 ? clamp1(fit.offsetY + (2 * dpy) / g.overflowY) : fit.offsetY,
  };
}
